<template>
  <WorkBaseRestrictedTextField
    ref="restrictedTextField"
    :class="[`
      w-number-field w-number-field__text-align-${textAlign} 
      ${forceMinWidth ? 'w-number-field__min-width' : ''}
      ${useFullHeight ? '' : 'no-full-height'}
    `]"
    :value="value"
    v-bind="$props"
    :char-whitelist="internalCharWhiteList"
    :always-allowed-keys="internalAlwaysAllowedKeys"
    :placeholder="internalPlaceholder"
    :input-get-formatter="invokeGetFormatter"
    :input-set-formatter="invokeSetFormatter"
    :next-to-each-other="nextToEachOther"
    :array-of-is-valid-functions="validFunctions"
    :with-decrease-increase-buttons="withDecreaseIncreaseButtons"
    :space-between="spaceBetween"
    @input="$emit('input', $event)"
  >
    <template
      v-if="withDecreaseIncreaseButtons"
      #prepend
    >
      <w-minus-button
        :disabled="minusButtonDisabled"
        @click="_handleMinus1"
      />
    </template>

    <template #append>
      <slot name="append" />
    </template>

    <template
      v-if="withDecreaseIncreaseButtons"
      #append-outer
    >
      <w-add-button
        :disabled="plusButtonDisabled"
        @click="_handlePlus1"
      />
    </template>
  </WorkBaseRestrictedTextField>
</template>

<script>
// components
import WorkBaseRestrictedTextField from '@/components/global_components/forms/text_fields/WorkBaseRestrictedTextField';

// mixins
import numberFieldMixin from '@/components/global_components/forms/text_fields/mixins/number-field-mixin';
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';

// helpers
import FORMAT_VALIDATIONS from '@/services/formatter_services/enums/FORMAT_VALIDATIONS';

export default {
  name: 'WorkNumberTextField',
  components: { WorkBaseRestrictedTextField },
  props: {
    value: { type: [Number, String] },
    textAlign: { type: String, default: 'end' },
    withDecreaseIncreaseButtons: { type: Boolean, default: false },
    nextToEachOther: { type: Boolean, default: false },
    minValue: { type: [Number, String] },
    maxValue: { type: [Number, String] },
    forceMinWidth: { type: Boolean, default: false },
    spaceBetween: { type: Boolean, default: false },
    useFullHeight: { type: Boolean, default: true },
  },
  data() {
    return {
      validFunctions: [
        (value) => {
          return (
            value === undefined ||
            value?.length === 0 ||
            ((Number(value) >= Number(this.internalMinValue) ||
              this.internalMinValue === undefined) &&
              (Number(value) <= Number(this.maxValue) || this.maxValue === undefined))
          );
        },
        () =>
          this.internalMinValue === undefined ||
          this.maxValue === undefined ||
          this.internalMinValue <= this.maxValue,
      ],
      internalMinValue: this.minValue,
    };
  },
  mixins: [
    isRequired,
    pendingAble,
    numberFieldMixin({
      setFormatter: (v) => {
        if (FORMAT_VALIDATIONS.ONLY_NEGATIVE_PREFIX(v)) {
          return;
        }
        return parseInt(v);
      },
    }),
  ],
  computed: {
    minusButtonDisabled() {
      if (!this._isValueValidInput(this.value) || this.disbled) {
        return true;
      }
      if (this.internalMinValue === undefined) {
        return false;
      }
      return this.value <= this.internalMinValue;
    },
    plusButtonDisabled() {
      if (!this._isValueValidInput(this.value) || this.disbled) {
        return true;
      }
      if (this.maxValue === undefined) {
        return false;
      }
      return this.value >= this.maxValue;
    },
    internalPlaceholder() {
      if (this.placeholder != undefined) {
        return this.placeholder;
      }
      return this.internalMinValue !== undefined
        ? this.internalMinValue.toString()
        : '';
    },
  },
  mounted() {
    //validate initial input
    if (!this._isValueValidInput(this.value)) {
      const validValue = this._isValueValidInput(this.internalMinValue)
        ? this.internalMinValue
        : '';
      this.$emit('input', validValue);
    }

    this.$emit('mounted');
  },
  methods: {
    _handleMinus1() {
      const newValue = (this.value ?? 0) - 1;

      if (this.withNegatives === false && newValue < 0) {
        return;
      }
      return this.$emit('input', newValue);
    },
    _handlePlus1() {
      const newValue = (this.value ?? 0) + 1;
      return this.$emit('input', newValue);
    },
    focus() {
      this.$refs.restrictedTextField.focus();
    },
    _isValueValidInput(value) {
      for (const valueIsValidFunc of this.validFunctions) {
        if (valueIsValidFunc(value) === false) {
          return false;
        }
      }
      return true;
    },
  },
  watch: {
    minValue: {
      handler(newValue) {
        if (!this.withNegatives) {
          if (this.minValue < 0 || this.minValue === undefined) {
            this.internalMinValue = 0;
          }
        }

        this.internalMinValue = newValue;
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.w-number-field__min-width ::v-deep {
  .v-input__control {
    min-width: 8ch !important;
  }
}

.w-number-field__text-align-start ::v-deep {
  .v-text-field__slot input {
    text-align: start !important;
  }
}

.w-number-field__text-align-end ::v-deep {
  .v-text-field__slot input {
    text-align: end !important;
  }
}

.w-number-field__text-align-center ::v-deep {
  .v-text-field__slot input {
    text-align: center !important;
  }
}

.no-full-height {
  height: auto !important;
}
</style>
